// src/Profile.js
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Footerbar from './components/Footerbar';
import useAppLayout from './hooks/useAppLayout';
import API_URL from './api/config';
import './styles/style.css';
import { toast, ToastContainer } from 'react-toastify';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import 'react-toastify/dist/ReactToastify.css';
import ReactModal from 'react-modal';
import imageCompression from 'browser-image-compression';
import ImageWithLoader2 from './components/ImageWithLoader2';

import { Worker, Viewer } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';

// Import the required CSS
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';


import {
  Upload, Camera, Trash2, Users, Heart, MapPin, Calendar, Phone, Mail,
  Home as HomeIcon, Church, User, UserCheck, UserX, Edit3, Plus, Pencil, Save,
  ChevronLeft, ChevronRight, X, FileText
} from 'lucide-react';

// A11y for modal
if (typeof document !== 'undefined') {
  ReactModal.setAppElement('#root');
}

/* ---------- tiny markdown -> html (safe-ish) ---------- */

/* ---------- BIO utilities ---------- */
function parseBioSections(content = '') {
  const lines = (content || '').split('\n');
  const sections = [];
  let i = 0;
  while (i < lines.length) {
    if (/^###\s+/.test(lines[i])) {
      const title = lines[i].replace(/^###\s+/, '').trim() || 'Untitled';
      const startLine = i;
      i++;
      const bodyLines = [];
      while (i < lines.length && !/^###\s+/.test(lines[i])) {
        bodyLines.push(lines[i]); i++;
      }
      const body = bodyLines.join('\n').trim();
      sections.push({ title, body, startLine, endLine: i - 1 });
    } else {
      const startLine = i;
      const bodyLines = [];
      while (i < lines.length && !/^###\s+/.test(lines[i])) {
        bodyLines.push(lines[i]); i++;
      }
      const body = bodyLines.join('\n').trim();
      if (body) sections.push({ title: 'Biography', body, startLine, endLine: i - 1 });
    }
  }
  return sections;
}
function replaceSection(content = '', sectionIdx, nextTitle, nextBody) {
  const lines = (content || '').split('\n');
  const sections = parseBioSections(content);
  if (sectionIdx < 0 || sectionIdx >= sections.length) return content;
  const target = sections[sectionIdx];
  const newBlock = [`### ${nextTitle || 'Untitled'}`, (nextBody || '').trim(), ''].join('\n');
  const before = lines.slice(0, target.startLine);
  const after  = lines.slice(target.endLine + 1);
  let merged = [...before, newBlock, ...after].join('\n');
  merged = merged.replace(/\n{3,}/g, '\n\n');
  return merged.trim() + '\n';
}
function deleteSection(content = '', sectionIdx) {
  const lines = (content || '').split('\n');
  const sections = parseBioSections(content);
  if (sectionIdx < 0 || sectionIdx >= sections.length) return content;
  const target = sections[sectionIdx];
  const before = lines.slice(0, target.startLine);
  const after  = lines.slice(target.endLine + 1);
  let merged = [...before, ...after].join('\n');
  merged = merged.replace(/\n{3,}/g, '\n\n').trim();
  return merged ? merged + '\n' : '';
}
function appendSection(content = '', title, body) {
  const block = `\n${content?.trim() ? '\n' : ''}### ${title || 'Untitled'}\n${(body || '').trim()}\n`;
  return (content || '').trim() ? (content.trim() + block) : block.trim() + '\n';
}

// image fallback
const withFallback = (src, fallback) => ({
  src, onError: (e) => (e.currentTarget.src = fallback), loading: 'lazy'
});

function Profile() {

  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  const { isSidebarVisible, showSidebar, toggleSidebar } = useAppLayout();
  const queryClient = useQueryClient();
  const location = useLocation();
  const memberId = location.state?.memberId;

  const loggedInUserId = localStorage.getItem("user_id");
  const loggedFamilyAdmin = localStorage.getItem("family_admin");

   


  /* ------------ Queries ------------ */
  const { data: coverData } = useQuery({
    queryKey: ['profileCover', memberId],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/profile-cover-api`, {
        withCredentials: true, params: { memberId },
      });
      return res.data;
    },
    enabled: !!memberId, staleTime: 0, cacheTime: Infinity,
  });



  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showCoverDropdown, setShowCoverDropdown] = useState(false);
  
  const toggleProfileDropdown = () => {
    setShowProfileDropdown(prev => !prev);
    setShowCoverDropdown(false); // optional: close other dropdown
  };
  
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  
  const [coverModalOpen, setCoverModalOpen] = useState(false);
   const [coverModalUploadedOpen, setCoverModalUploadedOpen] = useState(false);
  
   const [profileModalUploadedOpen, setProfileModalUploadedOpen] = useState(false);
  
   const [fileProfile, setFileProfile] = useState(null);
   const [fileNameProfile, setFileNameProfile] = useState('');
  
   const [fileCover, setFileCover] = useState(null);
   const [fileNameCover, setFileNameCover] = useState('');
   
   /*const handleFileChangeProfile = (e) => {
     const file = e.target.files[0];
     if (file) {
       setFile(file);        // For uploading
       setFileName(file.name); // For displaying the name
     }
   };*/
  
   const handleFileChangeProfile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
  
    try {
      const options = {
        maxSizeMB: 1,             // Compress to ~1MB
        maxWidthOrHeight: 800,    // Resize max dimension
        useWebWorker: true,       // Faster compression
      };
  
      // Compress image
      const compressedFile = await imageCompression(file, options);
  
      // Save compressed file (instead of original)
      setFileProfile(compressedFile);
      setFileNameProfile(file.name); // Keep the original file name for display
  
    } catch (err) {
      console.error('Image compression error:', err);
      toast.error('Failed to compress image.');
    }
  };
  
  
  const handleFileChangeCover = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
  
    try {
      const options = {
        maxSizeMB: 1,             // Compress to ~1MB
        maxWidthOrHeight: 800,    // Resize max dimension
        useWebWorker: true,       // Faster compression
      };
  
      // Compress image
      const compressedFile = await imageCompression(file, options);
  
      // Save compressed file (instead of original)
      setFileCover(compressedFile);
      setFileNameCover(file.name); // Keep the original file name for display
  
    } catch (err) {
      console.error('Image compression error:', err);
      toast.error('Failed to compress image.');
    }
  };
  
  
   //update profile
  
   const [isCompressing, setIsCompressing] = useState(false);
   const [isUploading, setIsUploading] = useState(false);
  
   const handleUploadProfile = async () => {
    if (!fileProfile) {
      toast.error("Please select an image first", { position: "top-right", autoClose: 3000 });
      return;
    }
  
    setIsCompressing(true); // Disable button during compression
  
    try {
      const targetKB = 100;
      let quality = 0.9;
      let compressedFile = fileProfile;
  
      // Iteratively compress to ~200KB or until quality hits 0.1
      while (true) {
        const options = {
          maxSizeMB: targetKB / 1024,
          initialQuality: quality,
          useWebWorker: true,
        };
  
        compressedFile = await imageCompression(fileProfile, options);
  
        if (compressedFile.size / 1024 <= targetKB || quality <= 0.1) break;
        quality -= 0.1;
      }
  
      setIsCompressing(false);
      setIsUploading(true); // Disable button during upload
  
      const formData = new FormData();
      formData.append('image_name', compressedFile);
  
      await axios.post(`${API_URL}/profile-image-update-api`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
  
      toast.success("Profile picture updated successfully!", { position: "top-right", autoClose: 3000 });
      queryClient.invalidateQueries(['userProfile']);// Refresh cover/profile image //coverImage
      setFileProfile(null); // Reset file input
      setProfileModalOpen(false); 
    } catch (err) {
      toast.error("Upload failed", { position: "top-right", autoClose: 3000 });
      console.error("Upload error:", err);
    } finally {
      setIsUploading(false);
      setIsCompressing(false);
    }
  };
  
  
  
  const handleUseProfileImage = async (imageName) => {
    try {
      await axios.put(`${API_URL}/profile-image-update-from-gallery`, 
        { image_name: imageName }, 
        {
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' }
        }
      );
  
      toast.success("Cover image updated from gallery", { position: "top-right", autoClose: 3000 });
      queryClient.invalidateQueries(['coverImage']); // Refresh
      setProfileModalOpen(false);
      setProfileModalUploadedOpen(false); // Close modal
    } catch (error) {
      console.error("Error updating cover from gallery:", error);
      toast.error("Failed to update cover image", { position: "top-right", autoClose: 3000 });
    }
  };
  
   //end update profile
  
   const [isCompressingCover, setIsCompressingCover] = useState(false);
  const [isUploadingCover, setIsUploadingCover] = useState(false);

  
  
  const handleUploadCover = async () => {
    if (!fileCover) {
      toast.error("Please select an image first", { position: "top-right", autoClose: 3000 });
      return;
    }
  
    setIsCompressingCover(true); // Disable button during compression
  
    try {
      const targetKB = 100;
      let quality = 0.9;
      let compressedFile = fileCover;
  
      // Iteratively compress until ~200KB or minimum quality
      while (true) {
        const options = {
          maxSizeMB: targetKB / 1024,
          initialQuality: quality,
          useWebWorker: true,
        };
  
        compressedFile = await imageCompression(fileCover, options);
  
        if (compressedFile.size / 1024 <= targetKB || quality <= 0.1) break;
        quality -= 0.1;
      }
  
      setIsCompressingCover(false);
      setIsUploadingCover(true); // Now uploading
  
      const formData = new FormData();
      formData.append('image_name', compressedFile);
  
      await axios.post(`${API_URL}/profile-cover-update-api`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
  
      toast.success("Cover image updated successfully!", { position: "top-right", autoClose: 3000 });
      //queryClient.invalidateQueries(['profileCover']); // Refresh UI coverImage
      queryClient.invalidateQueries(['coverImage']);
      
      setFileCover('');

      setFileCover(null); // Reset file input if desired
      setCoverModalOpen(false); // Close modal if you have one
    } catch (err) {
      toast.error("Upload failed", { position: "top-right", autoClose: 3000 });
      console.error("Upload error:", err);
    } finally {
      setIsUploadingCover(false);
      setIsCompressingCover(false);
    }
  };
  
  
   //end upload cover
  
   //use image
   const handleUseCoverImage = async (imageName) => {
     try {
       await axios.put(`${API_URL}/profile-cover-update-from-gallery`, 
         { image_name: imageName }, 
         {
           withCredentials: true,
           headers: { 'Content-Type': 'application/json' }
         }
       );
   
       toast.success("Cover image updated from gallery", { position: "top-right", autoClose: 3000 });
       queryClient.invalidateQueries(['coverImage']); // Refresh
       setCoverModalOpen(false);
       setCoverModalUploadedOpen(false); // Close modal
     } catch (error) {
       console.error("Error updating cover from gallery:", error);
       toast.error("Failed to update cover image", { position: "top-right", autoClose: 3000 });
     }
   };
  
   //end use image
  


  const { data } = useQuery({
    queryKey: ['userProfile', memberId],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/profile-api`, {
        withCredentials: true, params: { memberId },
      });
      return res.data;
    },
    enabled: !!memberId, staleTime: 0, cacheTime: Infinity,
  });

  const { data: relationsData } = useQuery({
    queryKey: ['familyRelations', memberId],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/family-relations`, {
        withCredentials: true, params: { memberId },
      });
      return res.data;
    },
    enabled: !!memberId, staleTime: 0, cacheTime: Infinity,
  });

  const { data: bioData, refetch: refetchBio } = useQuery({
    queryKey: ['profileBio', memberId],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/profile-bio-api`, {
        withCredentials: true, params: { memberId },
      });
      return res.data;
    },
    enabled: !!memberId, staleTime: 0, cacheTime: Infinity,
  });

  const { data: gallery } = useQuery({
    queryKey: ['uploadedGallery', memberId],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/profile-uploaded-image-api`, {
        withCredentials: true, params: { memberId },
      });
      if (res.data.Status === 'Success') return res.data.images;
      throw new Error('Failed to fetch gallery images');
    },
    enabled: !!memberId, staleTime: 0, cacheTime: Infinity,
  });

  /* ------------ Local state ------------ */
  const [profile, setProfile] = useState({
    name: '', last_name: '', date_of_birth: '', gender: '',
    phone_number: '', phone_number_2: '', email: '', status: '',
    hometown: '', place_of_birth: '', religion: '', married: '',
    profile_image: '',
  });
  const [activeTab, setActiveTab] = useState('personal');

  // uploads
 
  const [coverUploading, setCoverUploading] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);

  // bio editor
  const [sectionModalOpen, setSectionModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [sectionTitle, setSectionTitle] = useState('');
  const [sectionBody, setSectionBody] = useState('');
  const [bioContent, setBioContent] = useState('');

  // LIGHTBOX (new)
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(0);
  const [zoomed, setZoomed] = useState(false);

  // derived list of displayable images
  const displayImages = (gallery || []).filter(g => g?.image_name);

  const openLightbox = useCallback((idx) => {
    setLightboxIdx(idx);
    setZoomed(false);
    setLightboxOpen(true);
  }, []);
  const closeLightbox = useCallback(() => setLightboxOpen(false), []);
  const prevImg = useCallback(() => {
    setLightboxIdx((i) => (i - 1 + displayImages.length) % displayImages.length);
    setZoomed(false);
  }, [displayImages.length]);
  const nextImg = useCallback(() => {
    setLightboxIdx((i) => (i + 1) % displayImages.length);
    setZoomed(false);
  }, [displayImages.length]);

  // keyboard navigation in lightbox
  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') prevImg();
      if (e.key === 'ArrowRight') nextImg();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxOpen, closeLightbox, prevImg, nextImg]);

  useEffect(() => {
    if (data?.Status === 'Success') {
      const u = data.user;
      setProfile({
        name: u.name || '', last_name: u.last_name || '',
        date_of_birth: u.date_of_birth ? u.date_of_birth.substring(0, 10) : '',
        gender: u.gender || '', phone_number: u.phone_number || '',
        phone_number_2: u.phone_number_2 || '', email: u.email || '',
        status: u.status ?? '', hometown: u.hometown || '',
        place_of_birth: u.place_of_birth || '', religion: u.religion || '',
        married: u.married || '', profile_image: u.profile_image || '',
      });
    }
  }, [data]);

  useEffect(() => {
    if (bioData?.Status === 'Success') {
      setBioContent(bioData?.bio?.content || '');
    }
  }, [bioData]);

  /* ------------ Helpers ------------ */
  const normalizeStatus = (s) => (s === 1 || s === '1' ? 1 : s === 0 || s === '0' ? 0 : null);
  const getStatusIcon = (status) => {
    const s = normalizeStatus(status);
    if (s === 0) return <UserCheck size={16} className="text-success" />;
    if (s === 1) return <UserX size={16} className="text-danger" />;
    return <User size={16} className="text-secondary" />;
  };
  const getStatusText = (status) => {
    const s = normalizeStatus(status);
    if (s === 0) return 'Alive';
    if (s === 1) return 'Deceased';
    return 'Unknown';
  };
  const imgUrl = (img) => (img ? `${API_URL}/images/${img}` : '/default-avatar.png');

  const isDeceasedProfile = normalizeStatus(profile?.status) === 1;

  const FamilyMemberRow = ({ member }) => {
    const name   = typeof member === 'string' ? member : (member?.name || '—');
    const status = typeof member === 'string' ? null   : (member?.status ?? null);
    const photoField =
      typeof member === 'string'
        ? null
        :  member?.image_name || null;

    const s = normalizeStatus(status);
    const dot = s === 1 ? 'bg-success' : s === 0 ? 'bg-danger' : 'bg-secondary';

    return (
      <div className="d-flex align-items-center py-2 border-bottom">
        <div className="position-relative me-3">

          <ImageWithLoader2 
          src={imgUrl(photoField)}
           
          placeholder="https://oret.africa/tree/img/placeholder-human-icon.jpg"
            fadeDuration={800}
              blurAmount={15}  
          alt={name} className="rounded-circle"
               style={{ width: 36, height: 36, objectFit: 'cover' }}
        />

          <span className={`position-absolute bottom-0 end-0 translate-middle p-1 border border-white rounded-circle ${dot}`} style={{ width: 8, height: 8 }}/>
        </div>
        <div className="flex-grow-1">
          <div className="fw-medium text-truncate small">{name}</div>
          {s !== 1 && s !== null && (
            <div className="small text-muted d-flex align-items-center gap-1">
              {getStatusIcon(status)} <span>{getStatusText(status)}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  /* ------------ Uploads ------------ */
  const MAX_FILES = 12;
  const MAX_MB_PER_FILE = 8;

  const validateFiles = (list) => {
    if (list.length > MAX_FILES) { toast.error(`Please select at most ${MAX_FILES} images`); return false; }
    for (const f of list) {
      if (!/^image\//.test(f.type)) { toast.error(`Unsupported file type: ${f.name}`); return false; }
      if (f.size > MAX_MB_PER_FILE * 1024 * 1024) { toast.error(`${f.name} is larger than ${MAX_MB_PER_FILE} MB`); return false; }
    }
    return true;
  };
 
 

function RelationCard({ title, count = 0, variant = 'default', icon, children, empty }) {
  return (
    <div className={`relation-card relation-${variant} card mb-4 shadow-sm`}>
      <div className="relation-head d-flex align-items-center gap-2">
        <div className="relation-icon">{icon}</div>
        <h6 className="mb-0">
          {title} {typeof count === 'number' ? <span className="text-muted">({count})</span> : null}
        </h6>
      </div>
      <div className="relation-body">
        {empty ? <div className="empty-pill">{empty}</div> : children}
      </div>
    </div>
  );
}


 const [uploadImageModalOpen, setImageUploadModalOpen] = useState(false);
  const [files, setFiles] = useState([]);

  const [description, setDescription] = useState("");
const [isPrivate, setIsPrivate] = useState(false); // checkbox state

    /*const handleFileChangeGallery = (e) => {
      setFiles([...e.target.files]);
    };*/

     const handleFileChangeGallery = async (e) => {
        const selectedFiles = Array.from(e.target.files);
        if (!selectedFiles.length) return;
    
        try {
          const options = {
            maxSizeMB: 1,            // Max file size (MB) after compression
            maxWidthOrHeight: 800,   // Resize if larger
            useWebWorker: true,      // For faster compression
          };
    
          const compressedFiles = await Promise.all(
            selectedFiles.map(async (file) => {
              try {
                const compressed = await imageCompression(file, options);
                return compressed;
              } catch (err) {
                console.error(`Compression failed for ${file.name}:`, err);
                toast.error(`Failed to compress ${file.name}`);
                return null; // Skip failed ones
              }
            })
          );
    
          // Filter out any failed compressions
          const validCompressedFiles = compressedFiles.filter(Boolean);
    
          // Update your state with the compressed files
          setFiles(validCompressedFiles);
    
          console.log("Compressed files:", validCompressedFiles);
        } catch (err) {
          console.error("Image compression error:", err);
          toast.error("Failed to compress images.");
        }
      }; 



 const [isUploadingImage, setIsUploadingImage] = useState(false);

const handleImageUpload = async () => {
  if (files.length === 0) {
    toast.error("Please select at least one image", { position: "top-right", autoClose: 3000 });
    return;
  }

  setIsUploadingImage(true); // Disable button

  try {
    const compressedFiles = [];

    for (const file of files) {
      const targetKB = 100;
      let quality = 0.9;
      let compressedFile = file;

      // Iteratively compress until size is ≤ 200KB or quality hits 0.1
      while (true) {
        const options = {
          maxSizeMB: targetKB / 1024, // Convert KB to MB
          initialQuality: quality,
          useWebWorker: true,
        };

        compressedFile = await imageCompression(file, options);

        if (compressedFile.size / 1024 <= targetKB || quality <= 0.1) break;
        quality -= 0.1;
      }

      compressedFiles.push(compressedFile);
    }

    // Build FormData with compressed files
    const formData = new FormData();
    compressedFiles.forEach((file) => formData.append("images", file));
    formData.append("description", description);
    formData.append("status", isPrivate ? 1 : 0);

    await axios.post(
      `${API_URL}/profile-upload-image-api?memberId=${memberId}`,
      formData,
      {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );

    toast.success("Upload successful!", { position: "top-right", autoClose: 3000 });
    setFiles([]);
    setDescription("");
    setIsPrivate(false);
    setImageUploadModalOpen(false);

    queryClient.invalidateQueries(['uploadedGallery', memberId]);

  } catch (err) {
    toast.error("Upload failed", { position: "top-right", autoClose: 3000 });
    console.error("Upload error:", err);
  } finally {
    setIsUploadingImage(false); // Re-enable button
  }
};
  


  const handleDeleteImage = async (id) => {
   
    try {
      const res = await axios.delete(`${API_URL}/profile-delete-image-api/${id}`, {
        withCredentials: true,
      });
  
      if (res.data.Status === "Success") {
        toast.success("Image deleted successfully", { position: "top-right", autoClose: 3000 });
        queryClient.invalidateQueries(['uploadedGallery']);
      }
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Failed to delete image", { position: "top-right", autoClose: 3000 });
    }
  };


const [biographyModalOpen, setBiographyModalOpen] = useState(false);
const [title, setTitle] = useState('');
const [biodescription, setBioDescription] = useState('');

const [isSaving, setIsSaving] = useState(false);

const handleSaveBiography = () => {
if (!title.trim() || !biodescription.trim()) {
  toast.error("Both title and description are required");
  return;
}

setIsSaving(true); // disable button

axios.post(`${API_URL}/save-bioprofile-api`, {
  memberId,
  title,
  biodescription
}, { withCredentials: true })
  .then(() => {
    toast.success("Family history saved successfully");
    setBiographyModalOpen(false);
    setTitle('');
    setBioDescription('');

    queryClient.invalidateQueries(['profileBiography']);
  })
  .catch(err => {
    console.error("Error saving family description:", err);
    toast.error("Error saving family description");
  })
  .finally(() => {
    setIsSaving(false); // enable button back
  });
};



const [biographyList, setbiographyList] = useState([]);

const {
  data: biographyData,
  isLoading: isBiographyLoading,
  isError: isBiographyError,
  refetch: refetchBiography
} = useQuery({
  queryKey: ['profileBiography', memberId], 
  queryFn: () =>
    axios
      .get(`${API_URL}/get-bioprofile-api`, {
        params: { memberId },   
        withCredentials: true
      })
      .then(res => res.data),
  enabled: !!memberId, 
  staleTime: 0,
  cacheTime: Infinity,
});


useEffect(() => {
  if (biographyData?.success) {
    setbiographyList(biographyData.data);
  }
}, [biographyData]);



const [biographyEditModalOpen, setBiographyEditModalOpen] = useState(false);
const [editTitle, setEditTitle] = useState('');
const [editBioDescription, setEditBioDescription] = useState('');
const [editId, setEditId] = useState(null);

const openBiographyEditModal = (item) => {
setEditTitle(item.title);
setEditBioDescription(item.description);
setEditId(item.id);
setBiographyEditModalOpen(true);
};



const [isUpdating, setIsUpdating] = useState(false);

const handleUpdateBiography = () => {
if (!editTitle.trim() || !editBioDescription.trim()) {
  toast.error("Both title and description are required");
  return;
}

setIsUpdating(true); // disable button

axios.put(`${API_URL}/update-bioprofile-api/${editId}`, {
  title: editTitle,
  description: editBioDescription
}, { withCredentials: true })
  .then(() => {
    toast.success("Family history updated successfully");
    setBiographyEditModalOpen(false);
    setEditId(null);
    setEditTitle('');
    setEditBioDescription('');
    queryClient.invalidateQueries(['profileBiography']);
  })
  .catch(err => {
    console.error("Error updating family description:", err);
    toast.error("Error updating family description");
  })
  .finally(() => {
    setIsUpdating(false); // enable button again
  });
};


const [confirmModalOpen, setConfirmModalOpen] = useState(false);
const [pendingDeleteId, setPendingDeleteId] = useState(null);

const askDeleteConfirmation = (id) => {
  setPendingDeleteId(id);
  setConfirmModalOpen(true);
};



const [isDeleting, setIsDeleting] = useState(false);

const confirmDelete = () => {
setIsDeleting(true); // disable button

axios.delete(`${API_URL}/delete-bioprofile-api/${pendingDeleteId}`, { withCredentials: true })
  .then(() => {
    toast.success("Family history deleted successfully");
    queryClient.invalidateQueries(['profileBiography']);
  })
  .catch(err => {
    console.error("Error deleting family description:", err);
    toast.error("Error deleting family description");
  })
  .finally(() => {
    setIsDeleting(false);       // enable button back
    setConfirmModalOpen(false);
    setPendingDeleteId(null);
  });
};



const [tributeText, setTributeText] = useState('');
const [isPostingTribute, setIsPostingTribute] = useState(false);


const {
  data: tributes = [],
  isLoading: isLoadingTributes
} = useQuery({

  queryKey: ['profileTribute', memberId],

  queryFn: async () => {
    const res = await axios.get(
      `${API_URL}/get-tributes-api/${memberId}`,
      { withCredentials: true }
    );

    return res.data.tributes;
  }
});

const handlePostTribute = () => {

  if (!tributeText.trim()) {
    toast.error("Tribute is required");
    return;
  }

  setIsPostingTribute(true);

  axios.post(`${API_URL}/save-tributes-api`, {
    memberId,
    tribute: tributeText
  }, { withCredentials: true })

  .then(() => {
    toast.success("Tribute saved");

    setTributeText(''); // clear textarea

    queryClient.invalidateQueries(['profileTribute', memberId]);
  })

  .catch(err => {
    console.error("Error saving tribute:", err);
    toast.error("Failed to save tribute");
  })

  .finally(() => {
    setIsPostingTribute(false);
  });
};


const [tributeEditModalOpen, setTributeEditModalOpen] = useState(false);
const [editTributeDescription, setEditTributeDescription] = useState('');
const [selectedTributeId, setSelectedTributeId] = useState(null);
const [isUpdatingTributes, setIsUpdatingTributes] = useState(false);

const openEditModal = (tribute) => {
  setSelectedTributeId(tribute.id);
  setEditTributeDescription(tribute.tribute);
  setTributeEditModalOpen(true);
};


const handleUpdateTributes = () => {
  if (!editTributeDescription.trim()) {
    toast.error("Tribute description is required");
    return;
  }

  setIsUpdatingTributes(true);

  axios.put(`${API_URL}/update-tribute-api`, {
    tributeId: selectedTributeId,
    tribute: editTributeDescription
  }, { withCredentials: true })
  .then(() => {
    toast.success("Tribute updated successfully");
    setTributeEditModalOpen(false);
    setEditTributeDescription('');
    setSelectedTributeId(null);

    // Refresh list
    queryClient.invalidateQueries(['profileTribute', memberId]);
  })
  .catch(err => {
    console.error("Error updating tribute:", err);
    toast.error("Failed to update tribute");
  })
  .finally(() => {
    setIsUpdatingTributes(false);
  });
};


const [deleteModalOpen, setDeleteModalOpen] = useState(false);
const [tributeToDelete, setTributeToDelete] = useState(null);
const [isDeletingTribute, setIsDeletingTribute] = useState(false);


const handleDeleteTribute = () => {
  if (!tributeToDelete) return;

  setIsDeletingTribute(true);

  axios.delete(`${API_URL}/delete-tribute-api/${tributeToDelete}`, { withCredentials: true })
    .then(() => {
      toast.success("Tribute deleted successfully");
      setDeleteModalOpen(false);
      setTributeToDelete(null);
      queryClient.invalidateQueries(['profileTribute', memberId]);
    })
    .catch(err => {
      console.error("Error deleting tribute:", err);
      toast.error("Failed to delete tribute");
    })
    .finally(() => {
      setIsDeletingTribute(false);
    });
};



 

const [eulogyText, setEulogyText] = useState('');
const [eulogyFile, setEulogyFile] = useState(null);
const [selectedPdfName, setSelectedPdfName] = useState('');
const [isSavingEulogy, setIsSavingEulogy] = useState(false);
const [eulogyModalOpen, setEulogyModalOpen] = useState(false);


const {
  data: eulogyData,
  isLoading: isLoadingEulogy
} = useQuery({
  queryKey: ['profileEulogy', memberId],
  queryFn: async () => {
    const res = await axios.get(
      `${API_URL}/get-eulogy-api/${memberId}`,
      { withCredentials: true }
    );
    return res.data.data;
  },
  enabled: !!memberId
});



useEffect(() => {
  if (eulogyModalOpen && eulogyData) {
    setEulogyText(eulogyData.eulogy || '');
    setSelectedPdfName(eulogyData.pdfName || '');
    setEulogyFile(null);
  }
}, [eulogyModalOpen, eulogyData]);


const handlePostEulogy = async () => {

  if (!eulogyText.trim()) {
    toast.error("Eulogy is required");
    return;
  }

  try {
    setIsSavingEulogy(true);

    const formData = new FormData();
    formData.append("memberId", memberId);
    formData.append("eulogy", eulogyText);

    if (eulogyFile) {
      formData.append("eulogyPdf", eulogyFile);
    }

    await axios.post(
      `${API_URL}/save-eulogy-api`,
      formData,
      {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      }
    );

    toast.success("Eulogy saved successfully");

    queryClient.invalidateQueries({
      queryKey: ['profileEulogy', memberId]
    });

    setEulogyModalOpen(false);

  } catch (err) {
    console.error("Error saving eulogy:", err);
    toast.error("Failed to save eulogy");
  } finally {
    setIsSavingEulogy(false);
  }
};
  


const handleDeleteEulogy = () => {

  setEulogyModalOpen(true);

  axios.delete(`${API_URL}/delete-eulogy-api`, {
    data: { memberId },  
    withCredentials: true
  })
    .then(() => {
      toast.success("Eulogy deleted successfully");
      queryClient.invalidateQueries(['profileEulogy', memberId]);
      setEulogyModalOpen(false);
      setEulogyText('');
      setEulogyFile(null);
     

    })
    .catch(err => {
      console.error("Error deleting eulogy:", err);
      toast.error("Failed to delete eulogy");
    })
    .finally(() => {
      setEulogyModalOpen(false);
    });
};


  return (
    <div className="scrollPageContainer theme-light">
      <div className="main-wrapper">
        {isSidebarVisible && <Sidebar />}
        <Header toggleSidebar={toggleSidebar} />
        <Sidebar show={showSidebar} toggle={toggleSidebar} />
        <Footerbar toggleSidebar={toggleSidebar} />
        <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

        <div className="page-wrapper">
          <div className="content container-fluid  ">

            {/* Cover + Avatar (with side gutters) */}
            
            <div className="row mb-2 px-2x">
              <div className="col-md-12 rounded-4 overflow-hiddenx  " 
              >
                <div className="rounded-4"  style={{
                height: 220,
                background: 'linear-gradient(135deg, var(--brand) 0%, var(--brand-dark) 100%)',
                position: 'relative'
              }}>


                {/* Cover Image */}
                                  {coverData?.cover_image ? (
                                    <ImageWithLoader2 
                                      src={`${API_URL}/images/${coverData.cover_image}`}
                                      placeholder="https://oret.africa/tree/img/imageplaceholder-icon.jpg"
                                        fadeDuration={800}
                                          blurAmount={15}  
                                      alt="Cover" 
                                      className="rounded-4"
                                      style={{  height: '220px', width: '100%', objectFit: 'cover' }}
                                    />
                                  ) : (
                                    <div>No cover image found</div>
                                  )}
                
                                  {/* Profile Image */}
                                  {profile.profile_image && (
                                    <div style={{
                                      position: 'absolute',
                                      bottom: '-5px', // half of profile image height (150px/2)
                                      left: '50%',
                                      transform: 'translateX(-50%)',
                                      width: '120px',
                                      height: '120px',
                                      borderRadius: '50%',
                                      overflow: 'hidden',
                                      border: '2px solid white', 
                                      backgroundColor: '#fff' ,
                                      zIndex: 10
                                    }}>
                                      {profile && (
                                        <ImageWithLoader2 
                                          src={`${API_URL}/images/${profile.profile_image}`} 
                                          placeholder="https://oret.africa/tree/img/placeholder-human-icon.jpg"
                                        fadeDuration={800}
                                          blurAmount={15} 
                                          alt="Profile" 
                                          style={{  width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                      )}
                
                                            {parseInt(loggedInUserId) === parseInt(memberId) && (
                                              <div
                  style={{
                    position: 'absolute',
                    bottom: '14px',   // 👈 move UP
                    right: '14px',    // 👈 move LEFT
                    zIndex: 10,
                  }}
                >
                  <button
                    onClick={() => setProfileModalOpen(true)}
                    style={{
                      background: '#59a50e',
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      border: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 0,
                    }}
                  >
                    <i
                      className="fas fa-camera"
                      style={{
                        color: '#ffffff',
                        fontSize: '11px',
                        lineHeight: 1,
                      }}
                    />
                  </button>
                </div>
                
                
                                            )}
                       
                
                                    </div>
                                  )}



                


  <ReactModal isOpen={profileModalOpen} onRequestClose={() => setProfileModalOpen(false)} 
                      className="Modal  " overlayClassName="Overlay"

                       >
                            <div className="modal-header" style={{ padding: "10px 12px" }}>

                            <div className="d-flex align-items-center gap-2">
                                <span
                                  className="d-inline-flex align-items-center justify-content-center"
                                  style={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: 10,
                                    background: "rgba(117,180,55,.12)",
                                    border: "1px solid rgba(117,180,55,.22)",
                                    color: "#184A2C",
                                    fontWeight: 900,
                                    fontSize: 12,
                                  }}
                                >
                                  <i className="fas fa-camera" />
                                </span>

                                <h6 className="mb-0" style={{ fontWeight: 900, color: "#184A2C" }}>
                                  Update Profile
                                </h6>
                              </div>

                             <label></label>
                              <button type="button" className="btn-close modalCloseButton" 
                              style={{ transform: "scale(.9)" }}
                              onClick={() => setProfileModalOpen(false)}></button>
                            </div>
                    
                            <div className="modal-body"  >


                               {/* Selected file (compact) */}
    <div
      className="d-flex align-items-center justify-content-between gap-2 mb-2"
      style={{
        background: "rgba(117,180,55,.06)",
        border: "1px solid rgba(117,180,55,.18)",
        borderRadius: 12,
        padding: "8px 10px",
      }}
    >
      <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: 999,
            background: fileNameProfile ? "#75B437" : "#9ca3af",
            flexShrink: 0,
          }}
        />
        <span className="small" style={{ color: "#111827" }}>
          {fileNameProfile ? "Selected:" : "No file"}
        </span>

        {fileNameProfile && (
          <span
            className="small"
            title={fileNameProfile}
            style={{
              fontWeight: 800,
              color: "#111827",
              maxWidth: 170,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "inline-block",
            }}
          >
            {fileNameProfile}
          </span>
        )}
      </div>

      {(isCompressing || isUploading) && (
        <span className="small text-muted">
          <i className="fas fa-spinner fa-spin" />
        </span>
      )}
    </div>



    {/* Buttons (compact) */}
    <label
      htmlFor="imageUpload"
      className="btn w-100 d-flex align-items-center justify-content-center gap-2 mb-2"
      style={{
        borderRadius: 12,
        border: "1px solid rgba(117,180,55,.35)",
        background: "#fff",
        color: "#184A2C",
        fontWeight: 900,
        padding: ".55rem .75rem",
      }}
    >
      <i className="fa fa-upload" />
      Choose image
    </label>

    <input
      type="file"
      id="imageUpload"
      name="image"
      required
      onChange={handleFileChangeProfile}
      style={{ display: "none" }}
      accept="image/*"
    />

    <button
      onClick={handleUploadProfile}
      className="btn w-100 d-flex align-items-center justify-content-center gap-2 mb-2"
      disabled={isCompressing || isUploading}
      style={{
        borderRadius: 12,
        fontWeight: 900,
        padding: ".55rem .75rem",
        backgroundImage: "linear-gradient(135deg, #75B437, #184A2C)",
        border: "none",
        color: "#fff",
      }}
    >
      {(isCompressing || isUploading) ? (
        <>
          <i className="fas fa-spinner fa-spin" />
          Updating…
        </>
      ) : (
        <>
          <i className="fas fa-check-circle" />
          Update
        </>
      )}
    </button>


    <button
      type="button"
      onClick={() => setProfileModalUploadedOpen(true)}
      className="btn w-100 d-flex align-items-center justify-content-center gap-2"
      style={{
        borderRadius: 12,
        border: "1px dashed rgba(24,74,44,.28)",
        background: "rgba(24,74,44,.06)",
        color: "#184A2C",
        fontWeight: 900,
        padding: ".55rem .75rem",
      }}
    >
      <i className="fas fa-images" />
      From uploads
    </button>

                     
                    
                            </div>
                    
                          </ReactModal> 


  <ReactModal isOpen={profileModalUploadedOpen} onRequestClose={() => setProfileModalUploadedOpen(false)} 
        className="Modal  " overlayClassName="Overlay"
        
      >
              <div className="modal-header  " style={{background: '#ffffff'}}>
               <label></label>
          <button type="button" className="btn-close modalCloseButton" onClick={() => setProfileModalUploadedOpen(false)}></button>
        </div>

        <div className="modal-body" style={{height: '460px', overflowY: 'auto'}}>

        <div className="row" >

        {gallery && gallery.length > 0 ? (
                        gallery.map((image) => (
                          <div className='col-6 col-md-6' key={image.id} style={{ textAlign: "center", marginBottom: '2%' }}>

                            <img 
                              src={`${API_URL}/images/${image.image_name}`} 
                              alt={image.description} 
                              style={{ width: '100%', height: '200px', objectFit: 'cover', marginBottom: '1%' }}
                            />

                            <button
                              className="btn btn-sm btn-outline-primary1 mt-1"
                              onClick={() => handleUseProfileImage(image.image_name)}
                            >
                              Use Image
                            </button>

                          </div>
                        ))
                      ) : (
                        <p>No gallery images found.</p>
                      )}
          
          </div>
        </div>

       
      </ReactModal> 



                               
                      

    
           

          {parseInt(loggedInUserId) === parseInt(memberId) && (
               <div
               style={{
                position: 'absolute', bottom: '10px', right: '10px', zIndex: 10
               }}
             >
                  <button
                  className="btn btn-sm btn-white"
                  onClick={() => setCoverModalOpen(true)}
                  style={{ background: '#75B437' }}
                  >
                  <i className="fas fa-camera" style={{ color: '#ffffff', fontSize: '16px' }}></i>
                  </button>
                  </div>

            )}



<ReactModal isOpen={coverModalOpen} onRequestClose={() => setCoverModalOpen(false)} 
                      className="Modal modal-lg" overlayClassName="Overlay"
                        >
                            <div className="modal-header  " >

                            <div className="d-flex align-items-center gap-2">
      <span
        className="d-inline-flex align-items-center justify-content-center"
        style={{
          width: 28,
          height: 28,
          borderRadius: 10,
          background: "rgba(117,180,55,.12)",
          border: "1px solid rgba(117,180,55,.22)",
          color: "#184A2C",
          fontWeight: 900,
          fontSize: 12,
        }}
      >
        <i className="fas fa-camera" />
      </span>

      <h6 className="mb-0" style={{ fontWeight: 900, color: "#184A2C" }}>
        Update Cover
      </h6>
    </div>

                             <label></label>
                              <button type="button" className="btn-close modalCloseButton" onClick={() => setCoverModalOpen(false)}></button>
                            </div>
                    
                            <div className="modal-body" style={{ padding: "10px 12px" }}>
              


                     {/* Selected file (compact) */}
    <div
      className="d-flex align-items-center justify-content-between gap-2 mb-2"
      style={{
        background: "rgba(117,180,55,.06)",
        border: "1px solid rgba(117,180,55,.18)",
        borderRadius: 12,
        padding: "8px 10px",
      }}
    >
      <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: 999,
            background: fileNameCover ? "#75B437" : "#9ca3af",
            flexShrink: 0,
          }}
        />
        <span className="small" style={{ color: "#111827" }}>
          {fileNameCover ? "Selected:" : "No file"}
        </span>

        {fileNameCover && (
          <span
            className="small"
            title={fileNameCover}
            style={{
              fontWeight: 800,
              color: "#111827",
              maxWidth: 170,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "inline-block",
            }}
          >
            {fileNameCover}
          </span>
        )}
      </div>

      {(isCompressing || isUploading) && (
        <span className="small text-muted">
          <i className="fas fa-spinner fa-spin" />
        </span>
      )}
    </div>




                     {/* Buttons (compact) */}
    <label
      htmlFor="imageUpload"
      className="btn w-100 d-flex align-items-center justify-content-center gap-2 mb-2"
      style={{
        borderRadius: 12,
        border: "1px solid rgba(117,180,55,.35)",
        background: "#fff",
        color: "#184A2C",
        fontWeight: 900,
        padding: ".55rem .75rem",
      }}
    >
      <i className="fa fa-upload" />
      Choose image
    </label>

    <input
      type="file"
      id="imageUpload"
      name="image"
      required
      onChange={handleFileChangeCover}
      style={{ display: "none" }}
      accept="image/*"
    />



                  <button
      onClick={handleUploadCover}
      className="btn w-100 d-flex align-items-center justify-content-center gap-2 mb-2"
      disabled={isCompressingCover || isUploadingCover}
      style={{
        borderRadius: 12,
        fontWeight: 900,
        padding: ".55rem .75rem",
        backgroundImage: "linear-gradient(135deg, #75B437, #184A2C)",
        border: "none",
        color: "#fff",
      }}
    >
       {isCompressingCover ? "Updating..." : isUploadingCover ? "Updating..." : "Update"}
    </button>  
                        
                    
                      


                          <button
      type="button"
      onClick={() => setCoverModalUploadedOpen(true)}
      className="btn w-100 d-flex align-items-center justify-content-center gap-2"
      style={{
        borderRadius: 12,
        border: "1px dashed rgba(24,74,44,.28)",
        background: "rgba(24,74,44,.06)",
        color: "#184A2C",
        fontWeight: 900,
        padding: ".55rem .75rem",
      }}
    >
      <i className="fas fa-images" />
      From uploads
    </button>

                     
                    
                            </div>
                    
                             
                          </ReactModal> 




   <ReactModal isOpen={coverModalUploadedOpen} onRequestClose={() => setCoverModalUploadedOpen(false)} 
        className="Modal modal-lg" overlayClassName="Overlay"
        >
              <div className="modal-header  " style={{background: '#ffffff'}}>
               <label></label>
          <button type="button" className="btn-close modalCloseButton" onClick={() => setCoverModalUploadedOpen(false)}></button>
        </div>

        <div className="modal-body" style={{height: '460px', overflowY: 'auto'}}>

        <div className="row" >


        {gallery && gallery.length > 0 ? (
                        gallery.map((image) => (
                          <div className='col-6 col-md-6' key={image.id} style={{ textAlign: "center", marginBottom: '2%' }}>

                            <img 
                              src={`${API_URL}/images/${image.image_name}`} 
                              alt={image.description} 
                              style={{ width: '100%', height: '250px', objectFit: 'cover', marginBottom: '1%' }}
                            />

                            <button
                              className="btn btn-sm btn-outline-primary1 mt-1"
                              onClick={() => handleUseCoverImage(image.image_name)}
                            >
                              Use Image
                            </button>

                          </div>
                        ))
                      ) : (
                        <p>No gallery images found.</p>
                      )}
          
          </div>
        </div>

         
      </ReactModal> 



                </div>
               


              </div>
            </div> 

              
              <div className='row'>
                <div className='col-md-12'>
                    {/* Name + status */}
            <div className="text-center mt-4 mb-4">
              <h1 className="fw-bold mb-2" style={{ fontSize: '1.5rem' }}>
              {profile?.n_prefix} {profile?.name} {profile?.last_name}
              </h1>
              <div className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill bg-light shadow-sm">
                {getStatusIcon(profile?.status)}
                <span className="fw-medium text-dark">{getStatusText(profile?.status)}</span>
              </div>
            </div>
                </div>
              </div>


              {/*<div className='row'>
                  <div className='col-md-12'>
                      
            <div className="d-flex justify-content-center mb-5">
              <div className="nav nav-pills bg-white border rounded-4 shadow-sm p-2" style={{ minWidth: 360 }}>
                {[
                  { key: 'personal', label: 'Info',   icon: <User size={18} /> },
                  { key: 'bio',      label: 'Bio',    icon: <Edit3 size={18} /> },
                  { key: 'photos',   label: 'Photos', icon: <Camera size={18} /> },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    className={`nav-link flex-fill d-flex align-items-center justify-content-center gap-2 border-0 rounded-3 ${
                      activeTab === tab.key ? 'active btn-brand text-white' : 'bg-transparent text-muted'
                    }`}
                    onClick={() => setActiveTab(tab.key)}
                    style={{ padding: '12px 20px', fontSize: '0.95rem', fontWeight: 500 }}
                  >
                    {tab.icon}{tab.label}
                  </button>
                ))}
              </div>
            </div>
                      
                  </div>
                </div>*/}


                <div className='row'>
                                  <div className='col-md-12'>
                
                                      {/* Tabs */}
                            <div className="d-flex justify-content-center mb-5">
                              <div className={`nav nav-pills bg-white border rounded-4 shadow-sm p-2 ${isDeceasedProfile ? 'profile-tabs-deceased' : ''}`} style={{ minWidth: 360 }}>
                                {(isDeceasedProfile
                                  ? [
                                      { key: 'personal', label: 'Info',   icon: <User size={18} /> },
                                      { key: 'bio',      label: 'Bio',    icon: <Edit3 size={18} /> },
                                      { key: 'photos',   label: 'Photos', icon: <Camera size={18} /> },
                                      { key: 'tributes', label: 'Tributes', icon: <Heart size={18} /> },
                                    ]
                                  : [
                                      { key: 'personal', label: 'Info',   icon: <User size={18} /> },
                                      { key: 'bio',      label: 'Bio',    icon: <Edit3 size={18} /> },
                                      { key: 'photos',   label: 'Photos', icon: <Camera size={18} /> },
                                    ]
                                ).map((tab) => (
                                  <button
                                    key={tab.key}
                                    className={`nav-link flex-fill d-flex align-items-center justify-content-center gap-2 border-0 rounded-3 ${
                                      activeTab === tab.key ? 'active btn-brand text-white' : 'bg-transparent text-muted'
                                    }`}
                                    onClick={() => setActiveTab(tab.key)}
                                    style={{ padding: '12px 20px', fontSize: '0.95rem', fontWeight: 500 }}
                                  >
                                    {tab.icon}{tab.label}
                                  </button>
                                ))}
                
                                {isDeceasedProfile && (
                                  <button
                                    className={`nav-link d-flex align-items-center justify-content-center gap-2 border-0 rounded-3 profile-tab-eulogy ${
                                      activeTab === 'eulogy' ? 'active btn-brand text-white' : 'bg-transparent text-muted'
                                    }`}
                                    onClick={() => setActiveTab('eulogy')}
                                    style={{ padding: '12px 20px', fontSize: '0.95rem', fontWeight: 600 }}
                                  >
                                    <FileText size={18} /> Eulogy
                                  </button>
                                )}
                              </div>
                            </div>
                                      
                                  </div>
                                </div>


                <div className='row'>
                <div className='col-md-12'>

            {/* Main card */}
            <div className="card border-0 shadow-lg rounded-4 overflow-hidden">
              {/* PERSONAL */}
              {activeTab === 'personal' && (
                <div className="card-body px-3 py-2">
                  <div className="row g-5 mb-4">
                    <div className="col-md-6">
                      <div className="d-flex align-items-center mb-4">
                        <div className="rounded-3 p-2 me-3" style={{ background:'rgba(117,180,55,.12)' }}>
                          <User size={24} className="text-brand" />
                        </div>
                        <h4 className="fw-bold mb-0">Personal Information</h4>
                      </div>

                      <div className="vstack gap-3">
                        <div className="d-flex align-items-start bg-light rounded-3 p-3">
                          <Calendar size={20} className="me-3 mt-1 text-brand" />
                          <div>
                            <div className="text-muted small fw-medium">Date of Birth</div>
                            <div className="fw-semibold">{profile?.date_of_birth || '—'}</div>
                          </div>
                        </div>
                        <div className="d-flex align-items-start bg-light rounded-3 p-3">
                          <Phone size={20} className="me-3 mt-1 text-brand" />
                          <div>
                            <div className="text-muted small fw-medium">Phone</div>
                            <div className="fw-semibold">{profile?.phone_number || '—'}</div>
                          </div>
                        </div>
                        <div className="d-flex align-items-start bg-light rounded-3 p-3">
                          <Mail size={20} className="me-3 mt-1 text-brand" />
                          <div style={{ wordBreak: 'break-word' }}>
                            <div className="text-muted small fw-medium">Email</div>
                            <div className="fw-semibold">{profile?.email || '—'}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="d-flex align-items-center mb-4">
                        <div className="rounded-3 p-2 me-3" style={{ background:'rgba(117,180,55,.12)' }}>
                          <MapPin size={24} className="text-brand" />
                        </div>
                        <h4 className="fw-bold mb-0">Location & Background</h4>
                      </div>
                      <div className="vstack gap-3">
                        <div className="d-flex align-items-start bg-light rounded-3 p-3">
                          <HomeIcon size={20} className="me-3 mt-1 text-brand" />
                          <div>
                            <div className="text-muted small fw-medium">Hometown</div>
                            <div className="fw-semibold">{profile?.hometown || '—'}</div>
                          </div>
                        </div>
                        <div className="d-flex align-items-start bg-light rounded-3 p-3">
                          <MapPin size={20} className="me-3 mt-1 text-brand" />
                          <div>
                            <div className="text-muted small fw-medium">Place of Birth</div>
                            <div className="fw-semibold">{profile?.place_of_birth || '—'}</div>
                          </div>
                        </div>
                        <div className="d-flex align-items-start bg-light rounded-3 p-3">
                          <Church size={20} className="me-3 mt-1 text-brand" />
                          <div>
                            <div className="text-muted small fw-medium">Religion</div>
                            <div className="fw-semibold">{profile?.religion || '—'}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Family blocks */}


                  
                 <div className="mt-5">
  <div className="d-flex align-items-center mb-4">
    <div className="rounded-3 p-2 me-3" style={{ background:'rgba(117,180,55,.12)' }}>
      <Users size={24} className="text-brand" />
    </div>
    <h4 className="fw-bold mb-0">Family Relations</h4>
  </div>

  <div className="row g-4">
    {/* Parents */}
    <div className="col-md-6">
      <RelationCard
        title="Parents"
        count={(relationsData?.parents || []).length}
        variant="parents"
        icon={<Users size={18} />}
        empty={!(relationsData?.parents||[]).length && 'No parents found.'}
      >
        <div style={{ maxHeight: 220, overflowY: 'auto' }}>
          {(relationsData?.parents || []).map((p, i) => (
            <FamilyMemberRow key={`parent-${i}`} member={p} />
          ))}
        </div>
      </RelationCard>
    </div>

    {/* Spouse */}
    <div className="col-md-6">
      <RelationCard
        title="Spouse"
        count={(relationsData?.spouse ? 1 : 0)}
        variant="spouse"
        icon={<Heart size={18} />}
        empty={!relationsData?.spouse && 'No spouse found.'}
      >
        {relationsData?.spouse && <FamilyMemberRow member={relationsData.spouse} />}
      </RelationCard>
    </div>

    {/* Siblings */}
    <div className="col-md-6">
      <RelationCard
        title="Siblings"
        count={(relationsData?.siblings || []).length}
        variant="siblings"
        icon={<Users size={18} />}
        empty={!(relationsData?.siblings||[]).length && 'No siblings found.'}
      >
        <div style={{ maxHeight: 220, overflowY: 'auto' }}>
          {(relationsData?.siblings || []).map((s, i) => (
            <FamilyMemberRow key={`sib-${i}`} member={s} />
          ))}
        </div>
      </RelationCard>
    </div>

    {/* Children */}
    <div className="col-md-6">
      <RelationCard
        title="Children"
        count={(relationsData?.children || []).length}
        variant="children"
        icon={<Users size={18} />}
        empty={!(relationsData?.children||[]).length && 'No children found.'}
      >
        <div style={{ maxHeight: 220, overflowY: 'auto' }}>
          {(relationsData?.children || []).map((c, i) => (
            <FamilyMemberRow key={`child-${i}`} member={c} />
          ))}
        </div>
      </RelationCard>
    </div>
  </div>
</div>

                </div>
              )}

              {/* BIO */}
              {activeTab === 'bio' && (
                <div className="card-body p-0 px-3 py-2">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div className="d-flex align-items-center">
                      <div className="rounded-3 p-2 me-3" style={{ background:'rgba(117,180,55,.12)', fontSize: '14px' }}>
                        <Edit3 size={24} className="text-brand" />
                      </div>
                      <h5 className="fw-bold mb-0" style={{fontSize: '14px'}}>Bio</h5>
                    </div>


                  {( parseInt(loggedInUserId) === parseInt(memberId) ||
                      loggedFamilyAdmin === "1" ) && ( 
                    <button className="btn btn-brand d-inline-flex align-items-center gap-2 rounded-3 shadow-sm"
                            onClick={() => setBiographyModalOpen(true)}
                            style={{ padding: '8px 8px', fontSize: '14px' }}>
                      <Plus size={14} /> Add Biography
                    </button>

                  )}

                  </div>

                   

                  {biographyList.length === 0 && (
                      <div className="text-center py-5">
                        <div className="bg-light rounded-4 p-5">
                          <Edit3 size={38} className="text-muted mb-3" />
                          <h6 className="text-muted mb-2">No biography sections yet</h6>
                          <p className="text-muted mb-0">Click <strong>Add Section</strong> to start writing your story.</p>
                        </div>
                      </div>
                    )}

                  {biographyList.map(item => (
                      <div key={item.id} className="card border-0 bg-light rounded-3 shadow-sm">
                        <div className="card-body p-4">
                          <div className="d-flex align-items-start justify-content-between">
                            <div className="pe-3 flex-grow-1">
                              <h5 className="mb-3 fw-bold text-brand">{item.title}</h5>
                              <div className="text-muted lh-lg">

                                      {item.description}
                                   
                            </div>

                            {( parseInt(loggedInUserId) === parseInt(memberId) ||
                      loggedFamilyAdmin === "1" ) && ( 
                            <div className="d-flex gap-2 flex-shrink-0">
                              <button className="btn btn-outline-success btn-sm rounded-pill d-flex align-items-center justify-content-center"
                                       onClick={() => openBiographyEditModal(item)} title="Edit" style={{ width: 36, height: 36 }}>
                                <Pencil size={16} />
                              </button>
                              <button className="btn btn-outline-danger btn-sm rounded-pill d-flex align-items-center justify-content-center"
                                      onClick={() => askDeleteConfirmation(item.id)} title="Delete" style={{ width: 36, height: 36 }}>
                                <Trash2 size={16} />
                              </button>
                            </div>
                            )}
                          </div>
                        </div>
                      </div>
                      </div>
                    ))}
                    

                  
                </div>
              )}

              {/* PHOTOS */}
              {activeTab === 'photos' && (
                <div className="card-body p-0">
                  <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
                    <div className="d-flex align-items-center">
                      <div className="rounded-3 p-2 me-2" style={{ background:'rgba(117,180,55,.12)' }}>
                        <Camera size={20} className="text-brand" />
                      </div>
                      <h6 className="fw-bold mb-0">Gallery</h6>
                    </div>

                    {parseInt(localStorage.getItem("user_id")) === parseInt(memberId) && (
                    <button className="btn btn-brand d-inline-flex align-items-center gap-2 rounded-3"
                            onClick={() => setImageUploadModalOpen(true)}>
                      <Upload size={16} /> Upload
                    </button>
                    )}
                  </div>

                  {/* padded grid with delete on every tile */}
                  <div className="container-fluid px-1 py-3">
                    <div className="row row-cols-2 g-3">
                      {displayImages.map((image, idx) => (
                        <div key={image.id} className="col">
                          <div className="position-relative">
                            <div
                              className="ratio ratio-1x1 rounded-3 overflow-hidden shadow-sm"
                              role="button"
                              tabIndex={0}
                              onClick={() => openLightbox(idx)}
                              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && openLightbox(idx)}
                              title="Click to view"
                              style={{ cursor: 'zoom-in' }}
                            >
                              <img
                                src={`${API_URL}/images/${image.image_name}`}
                                onError={(e) => (e.currentTarget.src = '/default-cover.jpg')}
                                alt={image.description || 'gallery'}
                                className="w-100 h-100" style={{ objectFit: 'cover' }}
                              />
                            </div>

                            {/* actions for every tile */}
                            <div className="position-absolute top-0 end-0 m-2">
                              <div className="dropdown">
                                
                              {( parseInt(loggedInUserId) === parseInt(memberId) ||
                          loggedFamilyAdmin === "1" ) && (
                                <button
                                  className="btn btn-light btn-sm rounded-circle d-inline-flex align-items-center justify-content-center p-0"
                                  id={`dropdownMenu-${image.id}`}
                                  data-bs-toggle="dropdown" aria-expanded="false" aria-label="Open actions"
                                  style={{ width: 28, height: 28 }}
                                >
                                  ⋮
                                </button>
                                )}

                                <ul className="dropdown-menu dropdown-menu-end p-1" aria-labelledby={`dropdownMenu-${image.id}`}>
                                  <li>
                                    <button className="dropdown-item text-danger d-flex align-items-center gap-2 py-1 px-2"
                                            onClick={() => handleDeleteImage(image.id)}>
                                      <Trash2 size={14} /> Delete
                                    </button>
                                  </li>
                                </ul>
                                

                              </div>
                            </div>

                            {image.description && (
                              <div className="position-absolute bottom-0 start-0 end-0 p-2">
                                <span className="badge bg-dark bg-opacity-75 text-wrap w-100 text-start">
                                  {image.description}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}

                      {displayImages.length === 0 && (
                        <div className="col-12">
                          <div className="text-center py-5">
                            <Camera size={36} className="text-muted mb-2" />
                            <div className="text-muted">No photos yet</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}


             {/* TRIBUTES */}
                          {activeTab === 'tributes' && (
                            <div className="card-body p-4">
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <Heart size={18} className="text-brand" />
                                <h5 className="mb-0">Tributes</h5>
                              </div>
            
                              <div className="mb-3">
                              <textarea
                                    className="form-control rounded-3"
                                    rows={3}
                                    placeholder="Share a memory or tribute..."
                                    value={tributeText}
                                    onChange={(e) => setTributeText(e.target.value)}
                                    style={{ fontSize: '0.95rem', resize: 'vertical' }}
                                  />
                                <div className="d-flex justify-content-end mt-2">
                                  <button
                                    className="btn btn-primary rounded-3"
                                    onClick={handlePostTribute}
                                    disabled={isPostingTribute || !tributeText.trim()}
                                  >
                                    {isPostingTribute ? 'Posting...' : 'Post Tribute'}
                                  </button>
                                </div>
                              </div>
            
                              {isLoadingTributes ? (
  <div className="text-muted small">Loading tributes...</div>
) : tributes.length === 0 ? (
  <div className="text-muted small">No tributes yet.</div>
) : (
  <div className="d-flex flex-column gap-3">
    {tributes.map((t) => (
      <div
        key={t.id || `${t.author_id}-${t.created_at}`}
        className="position-relative p-3 rounded-3 border"
      >
        {/* Author and date */}
        <div className="d-flex justify-content-between align-items-start mb-1 position-relative">
          <div className="fw-semibold small">
            {t.author_name || t.author || 'Family Member'}
          </div>

          <div className="d-flex align-items-center gap-2">
          <div className="small">
            {t.created_at ? new Date(t.created_at).toLocaleDateString() : ''}
          </div>

          {/* Dropdown for Edit/Delete (top-right corner) */}
        {t.author_id === parseInt(loggedInUserId) && (
           

            <div className="dropdown">
              {/* Trigger button */}
              <button
                className="btn btn-sm btn-light rounded-circle p-0"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                style={{ width: 28, height: 28 , backgroundColor: '#184A2C', color: '#ffffff'}}
              >
                ⋮
              </button>

              {/* Dropdown menu */}
              <ul className="dropdown-menu dropdown-menu-end p-1">
                <li>
                  <button
                    className="dropdown-item d-flex align-items-center gap-2 py-1 px-2"
                    onClick={() => {
                      setSelectedTributeId(t.id);
                      setEditTributeDescription(t.tribute);
                      setTributeEditModalOpen(true);
                    }}
                  >
                    Edit
                  </button>
                </li>
                <li>
                  <button
                    className="dropdown-item text-danger d-flex align-items-center gap-2 py-1 px-2"
                    onClick={() => {
                      setTributeToDelete(t.id);
                      setDeleteModalOpen(true);
                    }}
                  >
                    Delete
                  </button>
                </li>
              </ul>
            </div>
           
        )}

</div>
        
        </div>

        {/* Tribute text */}
        <div className="small">{t.tribute}</div>

        
      </div>
    ))}
  </div>
)}

                            </div>
                          )}
            
                         

{/* EULOGY */}
              {activeTab === 'eulogy' && (
                <div className="card-body p-4">
                  <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
                    <div className="d-flex align-items-center gap-2">
                      <FileText size={18} className="text-brand" />
                      <h5 className="mb-0">Eulogy</h5>
                    </div>
                    
                      <button
                        className="btn btn-brand rounded-3"
                        onClick={() => setEulogyModalOpen(true)}
                      >
                        Update Eulogy
                      </button>
                    
                  </div>

                  {isLoadingEulogy ? (
  <div className="text-muted small">Loading eulogy...</div>
) : (
  <div className="d-flex flex-column gap-3">

    {/* PDF */}
    {eulogyData?.pdfUrl ? (
      <div className="border rounded-3 overflow-hidden">
        <div className="px-3 py-2 border-bottom bg-light fw-semibold small">
          PDF Eulogy {eulogyData?.pdfName ? `- ${eulogyData.pdfName}` : ''}
        </div>

        <div style={{ height: '560px' }}>

       <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
                <Viewer 
                    fileUrl={eulogyData.pdfUrl} 
                    plugins={[defaultLayoutPluginInstance]} 
                />
            </Worker>
            </div>

      </div>
    ) : (
      <div className="small text-muted">No PDF uploaded yet.</div>
    )}

    {/* Text */}
    <div className="border rounded-3 p-3">
      <div className="fw-semibold mb-2">Written Eulogy</div>

      {eulogyData?.eulogy?.trim() ? (
        <div style={{ whiteSpace: 'pre-wrap' }}>
          {eulogyData.eulogy}
        </div>
      ) : (
        <div className="small text-muted">No written eulogy yet.</div>
      )}
    </div>

  </div>
)}

                </div>
              )}




            </div>

            </div>
            </div>




            {/* BIO MODAL */}
              
              <ReactModal isOpen={biographyModalOpen} onRequestClose={() => setBiographyModalOpen(false)} 
                    className="Modal " overlayClassName="Overlay" >

                    <div className="modal-body">

                    <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="modal-title fw-bold mb-0">
                    {editingIndex === null ? 'Add Bio Section' : 'Edit Bio Section'}
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setBiographyModalOpen(false)} />
                </div>

                      <div className="mb-3">

                      <label className="form-label small fw-medium text-muted">Section Title</label>
                    <input
                      autoFocus type="text" className="form-control rounded-3 border-2"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="e.g., Early Life, Career, Achievements"
                      style={{ fontSize: '0.95rem' }}
                    />
                      </div>

                      <div className="mb-3">

                      <label className="form-label small fw-medium text-muted">Content</label>
                    <textarea
                      rows={4} className="form-control rounded-3 border-2"
                      value={biodescription}
                      onChange={(e) => setBioDescription(e.target.value)}
                      placeholder="Share the story, milestone, or memory..."
                      style={{ fontSize: '0.95rem', resize: 'vertical' }}
                    />
                      </div>
                    </div>

                    <div className="modal-footer  d-flex justify-content-between" >
                    <button type="button" className="btn btn-outline-secondary rounded-3" 
                    onClick={() => setBiographyModalOpen(false)}> Cancel </button>

                    <button 
                          className="btn btn-brand rounded-3 d-flex align-items-center gap-2" 
                          onClick={handleSaveBiography} 
                          disabled={isSaving}  // disable when saving
                        >
                           <Save size={16} /> {isSaving ? "Saving..." : "Save"}
                          
                        </button>
                      
                    </div>
                  </ReactModal>

                  
                    
<ReactModal isOpen={biographyEditModalOpen} onRequestClose={() => setBiographyEditModalOpen(false)}
                    className="Modal" overlayClassName="Overlay" >
                  
                    <div className="modal-body">
                      <div className="mb-3">
                      <label className="form-label small fw-medium text-muted">Section Title</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          placeholder="e.g., Early Life, Career, Achievements"
                        />
                      </div>
                  
                      <div className="mb-3">
                      <label className="form-label small fw-medium text-muted">Content</label>
                        <textarea
                          className="form-control"
                          rows="4"
                          value={editBioDescription}
                          onChange={(e) => setEditBioDescription(e.target.value)}
                          placeholder="Share the story, milestone, or memory..."
                        />
                      </div>
                    </div>
                  
                    <div className="modal-footer d-flex justify-content-between">

                    <button type="button" className="btn btn-outline-secondary rounded-3" 
onClick={() => setBiographyEditModalOpen(false)}> Cancel </button>

                    <button 
                          className="btn btn-brand rounded-3 d-flex align-items-center gap-2" 
                          onClick={handleUpdateBiography} 
                          disabled={isUpdating}
                        >
                          <Save size={16} /> {isUpdating ? "Updating..." : "Update"}
                        </button>
                    </div>
                  </ReactModal>
                                      
                                      
                  <ReactModal isOpen={confirmModalOpen} onRequestClose={() => setConfirmModalOpen(false)} className="Modal" overlayClassName="Overlay">
                    
                    <div className="modal-body">
                      <p>Are you sure you want to delete this biography? </p>
                    </div>
                    <div className="modal-footer d-flex justify-content-between">
                      <button className="btn btn-secondary me-2" onClick={() => setConfirmModalOpen(false)}>No</button>
                      <button 
                          className="btn btn-danger" 
                          onClick={confirmDelete} 
                          disabled={isDeleting}
                        >
                          {isDeleting ? "Deleting..." : "Yes, Delete"}
                        </button>
                    </div>
                  </ReactModal>


                                
            {/* UPLOAD MODAL */}

            <ReactModal
              isOpen={uploadImageModalOpen}
              onRequestClose={() => setImageUploadModalOpen(false)}
              shouldCloseOnOverlayClick shouldCloseOnEsc
              style={{
                overlay: {
                  position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 1050,
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                },
                content: {
                  position: 'relative', border: 'none', background: 'white', borderRadius: 12, padding: 0,
                  inset: 'auto', maxWidth: 600, width: '90%', maxHeight: '90vh', overflow: 'auto'
                }
              }}
            >
              <div className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="modal-title fw-bold mb-0">Upload Photos</h5>
                  <button type="button" className="btn-close" onClick={() => setImageUploadModalOpen(false)} />
                </div>
                <div>
                  <div className="mb-3">
                    <label htmlFor="imageUpload"
                           className="w-100 border border-2 border-dashed rounded-3 p-4 text-center bg-light"
                           style={{ cursor: 'pointer', borderColor: '#dee2e6' }}>
                      <div className="d-flex flex-column align-items-center">
                        <Upload size={32} className="text-brand mb-2" />
                        <div className="fw-medium text-dark">Click to select images</div>
                        <div className="text-muted small">Or drag and drop your photos here</div>
                        <div className="text-muted small mt-1">Max 12 files, 8MB each</div>
                      </div>
                    </label>
                    <input type="file" id="imageUpload" multiple accept="image/*" onChange={handleFileChangeGallery} className="d-none" />
                  </div>

                  {files.length > 0 && (
                    <div className="mb-3">
                      <div className="small fw-medium text-muted mb-2">Selected Files ({files.length})</div>
                      <div className="row g-2">
                        {files.slice(0, 6).map((file, i) => (
                          <div key={i} className="col-4">
                            <div className="ratio ratio-1x1 rounded-3 overflow-hidden bg-light shadow-sm">
                              <img src={URL.createObjectURL(file)} alt="preview" loading="lazy" style={{ objectFit: 'cover' }} />
                            </div>
                          </div>
                        ))}
                        {files.length > 6 && (
                          <div className="col-4">
                            <div className="ratio ratio-1x1 rounded-3 bg-secondary d-flex align-items-center justify-content-center">
                              <span className="text-white fw-bold">+{files.length - 6}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label small fw-medium text-muted">Description (Optional)</label>
                    <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)}
                              className="form-control rounded-3" placeholder="Add a description for your photos..."
                              style={{ fontSize: '0.9rem', resize: 'none' }} />
                  </div>

                  <div className="form-check mb-4">
                    <input className="form-check-input" type="checkbox" id="privateCheck"
                           checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} />
                    <label className="form-check-label small" htmlFor="privateCheck">
                      Make photos private (only visible to you)
                    </label>
                  </div>

                  <div className="d-flex gap-2 justify-content-end">
                    <button className="btn btn-outline-secondary rounded-3" onClick={() => setImageUploadModalOpen(false)}>
                      Cancel
                    </button>
                    <button className="btn btn-brand rounded-3 d-flex align-items-center gap-2"
                            onClick={handleImageUpload}    disabled={isUploadingImage}>
                      <Upload size={16} /> {isUploadingImage ? "Uploading..." : "Upload"}  {files.length > 0 && `(${files.length})`}
                    </button>
                  </div>
                </div>
              </div>
            </ReactModal>



            {/* LIGHTBOX MODAL (new) */}
            <ReactModal
              isOpen={lightboxOpen}
              onRequestClose={closeLightbox}
              shouldCloseOnOverlayClick
              shouldCloseOnEsc
              style={{
                overlay: {
                  position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.9)', zIndex: 1100,
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                },
                content: {
                  position: 'relative', inset: 'auto', border: 'none', background: 'transparent',
                  padding: 0, overflow: 'visible', maxWidth: '96vw', maxHeight: '96vh'
                }
              }}
            >
              {displayImages.length > 0 && (
                <div className="position-relative d-flex align-items-center justify-content-center" style={{ width: '100%', height: '100%' }}>
                  {/* Close */}
                  <button
                    className="btn btn-light position-absolute"
                    onClick={closeLightbox}
                    title="Close"
                    style={{ top: 12, right: 12, borderRadius: '999px', width: 40, height: 40, display: 'grid', placeItems: 'center' }}
                  >
                    <X size={18} />
                  </button>

                  {/* Prev */}
                  {displayImages.length > 1 && (
                    <button
                      className="btn btn-light position-absolute"
                      onClick={prevImg}
                      title="Previous"
                      style={{ left: 12, borderRadius: '999px', width: 44, height: 44, display: 'grid', placeItems: 'center' }}
                    >
                      <ChevronLeft size={22} />
                    </button>
                  )}

                  {/* Image */}
                  <div
                    className="d-flex align-items-center justify-content-center"
                    style={{ maxWidth: '96vw', maxHeight: '86vh', cursor: zoomed ? 'zoom-out' : 'zoom-in' }}
                    onClick={() => setZoomed(z => !z)}
                    title={zoomed ? 'Click to fit' : 'Click to zoom'}
                  >
                    <img
                      src={`${API_URL}/images/${displayImages[lightboxIdx].image_name}`}
                      alt={displayImages[lightboxIdx].description || `photo ${lightboxIdx + 1}`}
                      style={{
                        maxWidth: zoomed ? 'none' : '96vw',
                        maxHeight: zoomed ? 'none' : '86vh',
                        width: zoomed ? 'auto' : 'auto',
                        height: zoomed ? 'auto' : 'auto',
                        objectFit: zoomed ? 'contain' : 'contain'
                      }}
                    />
                  </div>

                  {/* Next */}
                  {displayImages.length > 1 && (
                    <button
                      className="btn btn-light position-absolute"
                      onClick={nextImg}
                      title="Next"
                      style={{ right: 12, borderRadius: '999px', width: 44, height: 44, display: 'grid', placeItems: 'center' }}
                    >
                      <ChevronRight size={22} />
                    </button>
                  )}

                  {/* Caption */}
                  {(displayImages[lightboxIdx].description || displayImages.length > 1) && (
                    <div className="position-absolute start-50 translate-middle-x text-center"
                         style={{ bottom: 10, color: '#fff', fontSize: 14, opacity: .9 }}>
                      <div className="px-3 py-2 bg-dark bg-opacity-50 rounded-pill d-inline-block">
                        <span className="me-2">
                          {displayImages[lightboxIdx].description || 'Photo'}
                        </span>
                        {displayImages.length > 1 && (
                          <span className="text-white-50">({lightboxIdx + 1}/{displayImages.length})</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </ReactModal>



            <ReactModal
  isOpen={tributeEditModalOpen}
  onRequestClose={() => setTributeEditModalOpen(false)}
  className="Modal"
  overlayClassName="Overlay"
>
  <div className="modal-body">
    <div className="mb-3">
      <label className="form-label small fw-medium text-muted">Tribute description</label>
      <textarea
        className="form-control"
        rows="4"
        value={editTributeDescription}
        onChange={(e) => setEditTributeDescription(e.target.value)}
        placeholder="Update tribute description..."
      />
    </div>
  </div>

  <div className="modal-footer d-flex justify-content-between">
    <button
      type="button"
      className="btn btn-outline-secondary rounded-3"
      onClick={() => setTributeEditModalOpen(false)}
    >
      Cancel
    </button>

    <button
      className="btn btn-brand rounded-3 d-flex align-items-center gap-2"
      onClick={handleUpdateTributes}
      disabled={isUpdatingTributes}
    >
      <Save size={16} /> {isUpdatingTributes ? "Updating..." : "Update"}
    </button>
  </div>
</ReactModal>   


<ReactModal
  isOpen={deleteModalOpen}
  onRequestClose={() => setDeleteModalOpen(false)}
  className="Modal"
  overlayClassName="Overlay"
>
  <div className="modal-body">
    <p>Are you sure you want to delete this tribute? This action cannot be undone.</p>
  </div>

  <div className="modal-footer d-flex justify-content-between">
    <button
      type="button"
      className="btn btn-outline-secondary rounded-3"
      onClick={() => setDeleteModalOpen(false)}
    >
      Cancel
    </button>

    <button
      className="btn btn-danger rounded-3 d-flex align-items-center gap-2"
      onClick={() => handleDeleteTribute()}
      disabled={isDeletingTribute}
    >
      {isDeletingTribute ? 'Deleting...' : 'Delete'}
    </button>
  </div>
</ReactModal>


          

<ReactModal
  isOpen={eulogyModalOpen}
  onRequestClose={() => setEulogyModalOpen(false)}
  className="Modal modal-lg"
  overlayClassName="Overlay"
>
  <div className="modal-header">
    <h5 className="mb-0">
      Eulogy - {profile?.name || ''} {profile?.last_name || ''}
    </h5>
    <button
      type="button"
      className="btn-close"
      onClick={() => setEulogyModalOpen(false)}
    />
  </div>

  <div className="modal-body" style={{ maxHeight: '76vh', overflowY: 'auto' }}>

    <div className="mb-4 p-3 rounded-3 border bg-light">

      {/* TEXTAREA */}
      <label className="form-label fw-semibold mb-2">
        Write or paste eulogy
      </label>

      <textarea
        className="form-control rounded-3 mb-3"
        rows={6}
        placeholder="Write the eulogy here..."
        value={eulogyText}
        onChange={(e) => setEulogyText(e.target.value)}
        style={{ fontSize: '0.95rem', resize: 'vertical' }}
      />

      {/* FILE INPUT */}
      <label htmlFor="imageUploadEdit" className="form-label fw-semibold mb-2">
      <i className="bi bi-upload" /> Upload PDF (optional) 
      </label>

      <input
        id="imageUploadEdit"
        type="file"
        accept="application/pdf"
        className="form-control"
        onChange={(e) => {
          const file = e.target.files[0];
          if (file) {
            setEulogyFile(file);
            setSelectedPdfName(file.name);
          }
        }}
      />

      {selectedPdfName && (
        <div className="small text-muted mt-2">
          Selected: {selectedPdfName}
        </div>
      )}

      {/* SAVE BUTTON */}
      <div className="d-flex justify-content-between mt-3">{/*d-flex justify-content-between*/}
      {eulogyData && (
        <button type="button" className="btn btn-outline-danger rounded-3" 
             onClick={handleDeleteEulogy}
             disabled={isSavingEulogy || !eulogyData}> 
             {isSavingEulogy ? 'Deleting...' : 'Delete'} </button>
             
      )}
        <button
          className="btn btn-primary rounded-3"
          onClick={handlePostEulogy}
          disabled={isSavingEulogy || !eulogyText.trim()}
        >
          {isSavingEulogy ? 'Saving...' : 'Update Eulogy'}
        </button>
      </div>

    </div>

  </div>
</ReactModal>






            <div className="row end-page-space" />
          </div>
        </div>
      </div>

      {/* THEME TOKENS (single green palette) */}
     <style>{`
  :root {
    --brand: #3c6f09;
    --brand-dark: #3b6a1f;
    --brand-rgb: 117,180,55;
  }

  .btn-brand {
    background: var(--brand);
    color: #fff !important;
    border: 0;
  }
  .btn-brand:hover { filter: brightness(.95); }

  .text-brand { color: var(--brand) !important; }

  .nav-pills .nav-link { transition: background .2s ease, color .2s ease; }
  .nav-pills .nav-link.active {
    background: var(--brand) !important;
    color: #fff !important;
  }
  .nav-pills .nav-link:not(.active) {
    color: var(--brand) !important;
    background-color: rgba(var(--brand-rgb), .10);
  }
  .nav-pills .nav-link:not(.active):hover {
    background-color: rgba(var(--brand-rgb), .18);
  }



  /* relation cards */
.relation-card{ border:1px solid rgba(0,0,0,.06); border-radius:14px; overflow:hidden; }
.relation-head{
  padding:.6rem .85rem;
  font-weight:700;
  border-bottom:1px solid rgba(0,0,0,.05);
  display:flex; align-items:center; gap:.5rem;
}
.relation-icon{ width:28px; height:28px; display:grid; place-items:center; border-radius:8px; background:rgba(0,0,0,.06); }
.relation-body{ padding:.5rem .75rem; background:#fff; }

.empty-pill{
  font-size:.9rem; color:#6c757d;
  background:rgba(0,0,0,.035);
  border:1px dashed rgba(0,0,0,.08);
  padding:.65rem .75rem; border-radius:.6rem;
}

/* Row polish inside cards (works with your existing FamilyMemberRow) */
.relation-body .d-flex.align-items-center.py-2{
  padding:.6rem .4rem!important; margin:0;
}
.relation-body .d-flex.align-items-center.py-2:hover{
  background:rgba(0,0,0,.03);
  border-radius:.6rem;
}

/* Color variants for fast visual distinction */
.relation-parents .relation-head { background:rgba(117,180,55,.12); color:#184A2C; }
.relation-spouse  .relation-head { background:rgba(117,180,55,.12); color:#184A2C; }
.relation-siblings .relation-head{ background:rgba(117,180,55,.12); color:#184A2C; }
.relation-children .relation-head{ background:rgba(117,180,55,.12); color:#184A2C; }

.relation-parents .relation-icon{ background:rgba(117,180,55,.2); }
.relation-spouse  .relation-icon{ background:rgba(117,180,55,.2); }
.relation-siblings .relation-icon{ background:rgba(117,180,55,.2); }
.relation-children .relation-icon{ background:rgba(117,180,55,.2); }

`}</style>


    </div>
  );
}

export default Profile;
